import React, { useCallback } from 'react';
import { Table, Button, Input, Space, Popconfirm } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { FN110, FN116 } from '../../../services/n202Api';
import { dateToInput } from '../helpers';

const { TextArea } = Input;

interface Tab6Props {
  formData: Partial<FN110>;
  rows: Partial<FN116>[];
  isEditing: boolean;
  onRowsChange: (updater: (prev: Partial<FN116>[]) => Partial<FN116>[]) => void;
}

const Tab6Messages: React.FC<Tab6Props> = ({
  formData, rows, isEditing, onRowsChange,
}) => {
  const handleCellChange = useCallback((idx: number, key: keyof FN116, value: FN116[keyof FN116]) => {
    onRowsChange(prev => prev.map((r, i) => i === idx ? { ...r, [key]: value } : r));
  }, [onRowsChange]);

  const handleAdd = useCallback(() => {
    const maxSeq = rows.reduce((max, r) => Math.max(max, r.fn116_02 || 0), 0);
    onRowsChange(prev => [...prev, {
      fn116_01: formData.fn110_01,
      fn116_02: maxSeq + 1,
      fn116_22: new Date().toISOString(),
    }]);
  }, [rows, formData.fn110_01, onRowsChange]);

  const handleDelete = useCallback((idx: number) => {
    onRowsChange(prev => prev.filter((_, i) => i !== idx).map((r, i) => ({ ...r, fn116_02: i + 1 })));
  }, [onRowsChange]);

  const columns: ColumnsType<Partial<FN116>> = [
    { title: <span data-contract-field="fn116_02">#</span>, width: 50, render: (_v, _r, i) => i + 1 },
    {
      title: <span data-contract-field="fn116_11">員工代碼</span>, dataIndex: 'fn116_11', width: 100,
      render: (v, _r, i) => isEditing
        ? <Input size="small" value={v} onChange={e => handleCellChange(i, 'fn116_11', e.target.value)} />
        : v,
    },
    {
      title: <span data-contract-field="fn116_12">員工姓名</span>, dataIndex: 'fn116_12', width: 100,
      render: (v, _r, i) => isEditing
        ? <Input size="small" value={v} onChange={e => handleCellChange(i, 'fn116_12', e.target.value)} />
        : v,
    },
    {
      title: <span data-contract-field="fn116_21">留言內容</span>, dataIndex: 'fn116_21',
      render: (v, _r, i) => isEditing
        ? <TextArea size="small" value={v} rows={2} onChange={e => handleCellChange(i, 'fn116_21', e.target.value)} />
        : v,
    },
    {
      title: <span data-contract-field="fn116_22">傳送日期</span>, dataIndex: 'fn116_22', width: 110,
      render: (v) => dateToInput(v),
    },
    {
      title: <span data-contract-field="fn116_03">傳送備註</span>, dataIndex: 'fn116_03',
      render: (v, _r, i) => isEditing
        ? <Input size="small" value={v} onChange={e => handleCellChange(i, 'fn116_03', e.target.value)} />
        : v,
    },
    {
      title: <span data-contract-field="fn116_23">傳送時間</span>, dataIndex: 'fn116_23', width: 90,
      render: (v, _r, i) => isEditing
        ? <Input size="small" value={v} onChange={e => handleCellChange(i, 'fn116_23', e.target.value)} placeholder="HH:mm" />
        : v,
    },
    ...(isEditing ? [{
      title: '操作', width: 60, align: 'center' as const,
      render: (_v: unknown, _r: Partial<FN116>, i: number) => (
        <Popconfirm title="確認刪除?" onConfirm={() => handleDelete(i)}>
          <Button type="link" size="small" danger icon={<DeleteOutlined />} />
        </Popconfirm>
      ),
    }] : []),
  ];

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '8px 12px', marginBottom: 12, alignItems: 'center', maxWidth: 300 }}>
        <span style={{ textAlign: 'right', color: 'rgba(0,0,0,0.65)', fontSize: 13 }}>留言筆數</span>
        <Input size="small" disabled value={String(rows.length || 0)} style={{ maxWidth: 80 }} />
      </div>
      {isEditing && (
        <Space style={{ marginBottom: 8 }}>
          <Button size="small" type="primary" icon={<PlusOutlined />} onClick={handleAdd}>新增留言</Button>
        </Space>
      )}
      <Table<Partial<FN116>>
        columns={columns}
        dataSource={rows}
        rowKey={(_r, i) => String(i)}
        size="small"
        pagination={false}
        bordered
        scroll={{ y: 400 }}
      />
    </div>
  );
};

export default React.memo(Tab6Messages);
